import { useMutation } from '@apollo/client'
import { Table, TableBody, TableCell, TableRow } from '@mui/material'
import { formatToDate } from 'brazilian-values'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { useState } from 'react'

import { FloraTypography, ModalBody, ModalFooter, ModalHeader, FloraButton, Flex } from '@grupoboticario/flora-react'

import ActionModal from '@/components/Modal/ActionModal'
import Actions from '@/components/Table/Generic/Actions/Actions'
import BackofficeTableContainer from '@/components/Table/Generic/BackofficeTableContainer/BackofficeTableContainer'
import TableHeader from '@/components/Table/Generic/TableHeader/TableHeader'
import TableHeaderActions from '@/components/Table/Generic/TableHeaderActions/TableHeaderActions'
import { useHeaderContext } from '@/context/header'
import { useUserContext } from '@/context/user'
import PATCH_CONTACT from '@/graphql/mutation/createContact'
import DELETE_CONTACT from '@/graphql/mutation/deleteContact'
import UPDATE_CONTACT from '@/graphql/mutation/updateContact'
import { PlusCircle } from '@/icons/PlusCircle'
import { BackofficeTableStyle } from '@/utils/customStyle/tableStyle'
import { Actions as ActionsPermissions } from '@/utils/dictionaries/actionsDictionary/actions'
import translateContactsLabel from '@/utils/format/translateContactsLabel'
import translateContactsTypes from '@/utils/format/translateContactsTypes'
import { columns as columnsEmail } from '@/utils/tableColumns/customerEmailsTable'
import { columns as columnsPhone } from '@/utils/tableColumns/customerPhonesTable'
import triggerToastSuccess from '@/utils/triggerToastSuccess/triggerToastSuccess'

import MultiContactTypeInput from './components/MultiContactTypeInput'
import * as S from './ContactsTable.styles'

const ContactsTable = ({ contactType, data, handleUpdate = () => {}, isLocal = false, isDisabled = false }) => {
  const isPhone = contactType === 'TELEPHONE'
  const isEmail = !isPhone
  const contactLabel = translateContactsLabel[contactType] || 'Contato desconhecido'
  const contacts = data?.filter((contact) => contact.contactMethod === contactType)
  const [deleteModal, setDeleteModal] = useState({
    value: { contactValue: '', type: '' },
    status: false
  })
  const [editModal, setEditModal] = useState({
    value: { contactValue: '', type: '' },
    status: false
  })
  const [addModal, setAddModal] = useState(false)
  const { member } = useHeaderContext()
  const { user, hasPermission } = useUserContext()
  const [createContact, { loading: loadingPatch }] = useMutation(PATCH_CONTACT, {
    onError: () => {}
  })
  const [deleteContact, { loading: loadingDelete }] = useMutation(DELETE_CONTACT, {
    onError: () => {}
  })
  const [updateContact, { loading: loadingUpdate }] = useMutation(UPDATE_CONTACT, {
    onError: () => {}
  })

  const handleChange = (event, values) => {
    if (event === 'delete') {
      setDeleteModal({ value: values, status: true })
    } else {
      setEditModal({ value: values, status: true })
    }
  }

  const handleRemoveLocal = () => {
    handleUpdate({ value: deleteModal.value.contactValue }, 'DELETE')
    setDeleteModal(false)
  }

  const handleRemoveRemote = async () => {
    const { error, errors } = await deleteContact({
      variables: {
        documentNumber: member?.documentNumber,
        value: deleteModal.value.contactValue
      }
    })

    triggerToastSuccess(
      error || errors,
      `${contactLabel} deletado com sucesso! O processamento das alterações pode levar até 5 minutos para ser concluído, por favor, aguarde.`,
      () => {
        handleUpdate()
        setDeleteModal(false)
      }
    )
  }

  const handleRemove = isLocal ? handleRemoveLocal : handleRemoveRemote

  const handleEdit = async (event, values) => {
    event.preventDefault()
    const { error, errors } = await updateContact({
      variables: {
        documentNumber: member?.documentNumber,
        value: editModal.value.contactValue,
        contact: {
          origin: {
            originId: 'backoffice-fidelidade',
            channel: 'crm',
            identifier: user.name
          },
          value: isPhone ? values.phone : values.email,
          contactMethod: contactType,
          contactType: values.type,
          documentNumber: member?.documentNumber
        }
      }
    })

    triggerToastSuccess(
      error || errors,
      `${contactLabel} editado com sucesso! O processamento das alterações pode levar até 5 minutos para ser concluído, por favor, aguarde.`,
      () => {
        handleUpdate()
        setEditModal(false)
      }
    )
  }

  const handleAddLocal = (event, values) => {
    event.preventDefault()
    handleUpdate(
      {
        value: isPhone ? values.phone : values.email,
        contactMethod: contactType,
        contactType: values.type
      },
      'ADD'
    )
    setAddModal(false)
  }

  const handleAddRemote = async (event, values) => {
    event.preventDefault()
    const { error, errors } = await createContact({
      variables: {
        documentNumber: member?.documentNumber,
        contact: {
          origin: {
            originId: 'backoffice-fidelidade',
            channel: 'crm',
            identifier: user.name
          },
          value: isPhone ? values.phone : values.email,
          contactMethod: contactType,
          contactType: values.type,
          documentNumber: member?.documentNumber
        }
      }
    })

    triggerToastSuccess(
      error || errors,
      `${contactLabel} criado com sucesso! O processamento das alterações pode levar até 5 minutos para ser concluído, por favor, aguarde.`,
      () => {
        handleUpdate()
        setAddModal(false)
      }
    )
  }

  const handleAdd = isLocal ? handleAddLocal : handleAddRemote

  const validationSchema = Yup.object().shape({
    showEmail: Yup.boolean(),
    showPhone: Yup.boolean(),
    email: Yup.string()
      .email('Digite um e-mail válido')
      .when('showEmail', {
        is: true,
        then: Yup.string().required('Digite um e-mail')
      }),
    phone: Yup.string().when('showPhone', {
      is: true,
      then: Yup.string()
        .matches(/^\+55.*$/gm, 'O número precisa começar com +55')
        .min(11, 'Digite um número válido')
        .max(19, 'Limite máximo de caracteres são 19')
    }),
    type: Yup.string().required('Selecione um tipo')
  })

  return (
    <>
      {contacts.length ? (
        <>
          <TableHeaderActions
            onActionClick={() => setAddModal(true)}
            rightActions={
              hasPermission(ActionsPermissions.REGISTER_CONTACT_INFO)
                ? [
                    {
                      event: 'add',
                      text: `Cadastrar ${contactLabel}`,
                      icon: <PlusCircle />
                    }
                  ]
                : []
            }
          />
          <BackofficeTableStyle />
          <BackofficeTableContainer columns={isPhone ? columnsPhone : columnsEmail} rows={3}>
            <Table aria-label={`Tabela de ${contactLabel}`}>
              <TableHeader columns={isPhone ? columnsPhone : columnsEmail} />
              <TableBody>
                {contacts.map((row) => {
                  return (
                    <TableRow key={row?.value}>
                      <TableCell>
                        <FloraTypography css={S.auxTextCss} fontSize="bodySmallStandard">
                          {row?.value}
                        </FloraTypography>
                      </TableCell>
                      <TableCell>
                        <FloraTypography css={S.auxTextCss} fontSize="bodySmallStandard">
                          {translateContactsTypes[row?.contactType]}
                        </FloraTypography>
                      </TableCell>
                      <TableCell>
                        <FloraTypography css={S.predominantTextCss} fontSize="bodySmallStandard">
                          {row?.updatedAt ? formatToDate(new Date(row?.updatedAt)) : '-'}
                        </FloraTypography>
                      </TableCell>
                      <Actions
                        actions={
                          isLocal
                            ? {
                                delete: true,
                                isDisabled: !hasPermission(ActionsPermissions.REGISTER_CONTACT_INFO)
                              }
                            : {
                                delete: true,
                                edit: true,
                                isDisabled: !hasPermission(ActionsPermissions.REGISTER_CONTACT_INFO)
                              }
                        }
                        data={row?.value}
                        onActionClick={(status) =>
                          handleChange(status, { contactValue: row?.value, type: row?.contactType })
                        }
                      />
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </BackofficeTableContainer>
        </>
      ) : (
        <Flex align="center" direction="column" gap="$2">
          <FloraTypography as="h6" css={S.centeredTitleCss} fontSize="subtitle">
            Não existem {isPhone ? 'telefones' : 'e-mails'} cadastrados
          </FloraTypography>
          <FloraButton
            disabled={!hasPermission(ActionsPermissions.UPDATE_REGISTER_DATA) || isDisabled}
            isUppercase
            onClick={() => setAddModal(true)}
            hierarchy="tertiary"
            has="iconRight"
            icon={<PlusCircle />}
          >
            Adicionar {isPhone ? 'telefone' : 'e-mail'}
          </FloraButton>
        </Flex>
      )}
      <ActionModal onOpenChange={setDeleteModal} open={deleteModal.status} size="small">
        <ModalHeader>Deletar {contactLabel.toLowerCase()}</ModalHeader>
        <ModalBody>
          <FloraTypography fontSize="bodyLargeStandard" fontWeight="bold">
            Tem certeza que deseja deletar este {contactLabel.toLowerCase()}?
          </FloraTypography>
        </ModalBody>
        <ModalFooter>
          <FloraButton
            data-testid="confirmDelete"
            disabled={!hasPermission(ActionsPermissions.UPDATE_REGISTER_DATA)}
            isLoading={loadingDelete}
            onClick={() => handleRemove()}
            variant="warning"
          >
            Deletar
          </FloraButton>
        </ModalFooter>
      </ActionModal>
      <ActionModal css={S.modalWrapperCss} onOpenChange={setEditModal} open={editModal.status} size="standard">
        <Formik
          initialValues={{
            showEmail: isEmail,
            showPhone: isPhone,
            email: isEmail ? editModal.value?.contactValue : '',
            phone: isPhone ? editModal.value?.contactValue : '',
            type: editModal.value?.type
          }}
          onSubmit={handleEdit}
          validationSchema={validationSchema}
        >
          {({ errors, values, handleChange: selectChange, isValid }) => (
            <>
              <ModalHeader>Editar {contactLabel.toLowerCase()}</ModalHeader>
              <ModalBody>
                <FloraTypography as="p" css={S.descriptionCss} fontSize="bodyLargeStandard">
                  Preencha e clique em salvar para atualizar:
                </FloraTypography>
                <MultiContactTypeInput errors={errors} isPhone={isPhone} selectChange={selectChange} />
              </ModalBody>
              <ModalFooter>
                <FloraButton
                  disabled={!isValid || !hasPermission(ActionsPermissions.UPDATE_REGISTER_DATA)}
                  isLoading={loadingUpdate}
                  onClick={(event) => handleEdit(event, values)}
                >
                  Salvar
                </FloraButton>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ActionModal>
      <ActionModal css={S.modalWrapperCss} onOpenChange={setAddModal} open={addModal} size="standard">
        <Formik
          initialValues={{
            showEmail: isEmail,
            showPhone: isPhone,
            email: '',
            phone: '+55',
            type: 'PERSONAL'
          }}
          onSubmit={handleEdit}
          validateOnMount
          validationSchema={validationSchema}
        >
          {({ errors, values, handleChange: selectChange, isValid }) => (
            <>
              <ModalHeader>Cadastrar {contactLabel.toLowerCase()}</ModalHeader>
              <ModalBody>
                <FloraTypography as="p" css={S.descriptionCss} fontSize="bodyLargeStandard">
                  Preencha e clique em salvar para novo cadastro:
                </FloraTypography>
                <MultiContactTypeInput errors={errors} isPhone={isPhone} selectChange={selectChange} />
              </ModalBody>
              <ModalFooter>
                <FloraButton disabled={!isValid} isLoading={loadingPatch} onClick={(event) => handleAdd(event, values)}>
                  Salvar
                </FloraButton>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ActionModal>
    </>
  )
}

export default ContactsTable
